/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import React, { useEffect } from 'react';
import { Grid, Row, Column, Button } from '@exo/frontend-components-base';
import { AuthCodeForm } from '@exo/frontend-components-forms';
import { ArrowRight16 } from '@carbon/icons-react/es';
import { useIntl } from '@exo/frontend-common-i18n';
import * as yup from 'yup';
import { onError } from '@apollo/client/link/error';
import * as S from './EnterCode.styles';
import { EnterCodeContainerRenderProps } from '@exo/frontend-features-insurance-polaris-login-logic';
import { useHistory } from 'react-router-dom';
import { patterns } from '@exo/frontend-common-insurance-schemas';
import { AuthEndpointsConfig } from '../../authConfig';

const str = text => <strong>{text}</strong>;

export const EnterCode = ({
  loadingUserDetails,
  onSubmit,
  userDetailsData,
  enterCodeError,
  isVerifying = false,
  intlPrefix,
  authConfig
}: EnterCodeContainerRenderProps & AuthConfig) => {
  const intl = useIntl(intlPrefix);
  const history = useHistory();

  const schema = yup.object().shape({
    userId: yup.string().required(intl.msg('form.userId.required', 'Invalid value') as string),
    companyEmail: yup
      .string()
      .required(intl.msg('form.companyEmail.required', 'Invalid value') as string),
    authorisationCode: yup
      .string()
      .required(intl.msg('form.authCode.required', 'Invalid value') as string),
    newPassword: yup
      .string()
      .required(intl.msg('form.newPassword.required', 'Invalid value') as string)
      .matches(patterns.new_password, {
        message: intl.msg('form.newPassword.error', 'Invalid value') as string,
        excludeEmptyString: true
      })
      .when('userId', {
        is: (userId: string) => !!userId,
        then: yup
          .string()
          .notOneOf(
            [yup.ref('userId')],
            intl.msg(
              'form.newPassword.PASSWORD_CANT_BE_THE_SAME_AS_USER_ID',
              'Your password cannot be the same as your userID, please try another one.'
            ) as string
          )
      }),
    newPasswordConfirmed: yup
      .string()
      .required(intl.msg('form.newPasswordConfirmed.required', 'Invalid value') as string)
      .oneOf(
        [yup.ref('newPassword')],
        intl.msg('form.newPasswordConfirmed.error', 'Invalid value') as string
      )
  });

  const goToLogin = () => {
    if (isVerifying) {
      window.location.href = `${authConfig?.mainAppUrl}/home/welcome`;
    } else {
      history.push('/auth/login');
    }
  };

  const handleFormSubmit = async d => {
    const res: boolean = await onSubmit(d);
    if (res) {
      history.push({
        pathname: isVerifying ? '/auth/new-user-verification/success' : '/auth/enter-code/success',
        state: isVerifying
      });
    }
  };

  useEffect(() => {
    if (enterCodeError) {
      history.push({
        pathname: isVerifying ? '/auth/new-user-verification/error' : '/auth/enter-code/error',
        state: {
          isVerifying,
          error: enterCodeError.response?.data?.message
        }
      });
    }
  }, [enterCodeError]);

  return (
    <AuthCodeForm
      mode="onSubmit"
      onSubmit={handleFormSubmit}
      schema={schema}
      data={{
        userId: userDetailsData?.username || '',
        companyEmail: userDetailsData?.companyEmail || '',
        authorisationCode: userDetailsData?.authCode !== 'null' ? userDetailsData?.authCode : '',
        newPassword: '',
        newPasswordConfirmed: ''
      }}
      dataId={loadingUserDetails}
      fields={[
        'userId',
        'companyEmail',
        'authorisationCode',
        'newPassword',
        'newPasswordConfirmed'
      ]}
      onError={onError}
      errors={enterCodeError}
      intlPrefix={intlPrefix}
      renderFooter={() => (
        <Grid>
          <Row>
            <Column>
              <S.FooterText>
                {
                  intl.msg(
                    'form.newPassword.error',
                    'Passwords must contain between eight and thirty characters, include at least one number, an upper-case letter and a lower-case letter. You may not repeat any of the last 8 passwords you have used. The supported special characters are: !#$()*+,-./:;=?@[\\]^_`{|}~'
                  ) as string
                }
                <br />
                <br />
                {
                  intl.msg('form.footerText', 'Clicking on <strong>Continue</strong> will', {
                    strong: str
                  }) as string
                }{' '}
                {isVerifying ? 'verify your account' : 'reset your password'}.
              </S.FooterText>
            </Column>
          </Row>
          <Row>
            <Column className="footerBtns">
              <Button
                variant="light"
                label="Cancel"
                className="cancel-button"
                onClick={goToLogin}
              />
              <Button type="submit" variant="light" label="Continue" icon={<ArrowRight16 />} />
            </Column>
          </Row>
        </Grid>
      )}
    />
  );
};

type AuthConfig = {
  authConfig?: AuthEndpointsConfig;
};
